import { useState, useEffect, useMemo } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth, API } from "@/App";
import axios from "axios";
import {
  Check,
  ChevronRight,
  Settings,
  Radio,
  Shield,
  ShieldCheck,
  Zap,
  Users,
  Music,
  MessageSquare,
  Bell,
  BarChart3,
  Lock,
  Headphones,
  CreditCard,
  Tag,
  Bot,
  ExternalLink,
  AlertTriangle,
  Star,
  Sparkles,
  RefreshCw,
  Play,
  MousePointerClick,
  Gamepad2,
  Layers,
  Rocket,
  Gift,
  Maximize2,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import AuthModal from "@/components/AuthModal";
import SiteFooter from "@/components/SiteFooter";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { PRODUCT_ALIASES } from "@/lib/productAliases";
import { ProductVideo, TrialCard, UpgradeCard, ProductChangelog, ProductReviews, defaultProductVideo } from "@/components/ProductExtras";
import ReviewCarousel from "@/components/ReviewCarousel";
import { saleBlock } from "@/lib/saleStatus";
import DeviceDemo, { hasDeviceDemo } from "@/components/DeviceDemo";

// Bot configurations
const BOTS_CONFIG = {
  gestion: {
    id: "gestion",
    name: "VantaPulse Gestion",
    tagline: "Le bot de gestion ultime",
    description: "Gérez votre serveur Discord comme un pro avec des outils de modération avancés, des systèmes de tickets, et bien plus encore.",
    logo: "/favicon.png",
    banner: "/images/banner-gestion.svg",
    color: "#5865F2",
    colorClass: "text-blue-500",
    bgClass: "bg-blue-500",
    bgLightClass: "bg-blue-500/10",
    borderClass: "border-blue-500",
    btnClass: "bg-blue-500 hover:bg-blue-600",
    glowClass: "shadow-[0_0_30px_rgba(88,101,242,0.3)]",
    icon: Settings,
    priceOffset: 0,
    features: [
      { icon: Shield, title: "Modération Avancée", description: "Auto-mod, anti-spam, anti-raid, logs complets" },
      { icon: MessageSquare, title: "Système de Tickets", description: "Tickets personnalisables avec transcriptions" },
      { icon: Users, title: "Gestion des Rôles", description: "Rôles automatiques, réactions, niveaux" },
      { icon: Bell, title: "Notifications", description: "Annonces, bienvenue, départ personnalisés" },
      { icon: BarChart3, title: "Statistiques", description: "Stats du serveur, activité, membres" },
      { icon: Lock, title: "Permissions", description: "Gestion fine des permissions et accès" },
    ],
    demoImages: [
      { url: "/images/gestion/demo-1.jpg", title: "Messages et embeds depuis le site" },
      { url: "/images/gestion/demo-2.jpg", title: "Tickets : panel, types et boutons" },
      { url: "/images/gestion/demo-3.jpg", title: "Sécurité : antiraid, antispam et sanctions" },
      { url: "/images/gestion/demo-4.jpg", title: "Créateur de serveur Discord avec l'IA" },
    ]
  },
  radio: {
    id: "radio",
    name: "VantaPulse Radio",
    tagline: "Le bot radio parfait",
    description: "Animez votre serveur avec de la musique de qualité, des playlists personnalisées, et des fonctionnalités radio uniques.",
    logo: "/favicon.png",
    banner: "/images/banner-radio.svg",
    color: "#22C55E",
    colorClass: "text-green-500",
    bgClass: "bg-green-500",
    bgLightClass: "bg-green-500/10",
    borderClass: "border-green-500",
    btnClass: "bg-green-500 hover:bg-green-600",
    glowClass: "shadow-[0_0_30px_rgba(34,197,94,0.3)]",
    icon: Radio,
    priceOffset: -0.50,
    features: [
      { icon: Music, title: "Musique HD", description: "Qualité audio supérieure, sans lag" },
      { icon: Headphones, title: "Playlists", description: "Créez et partagez vos playlists" },
      { icon: Radio, title: "Mode Radio", description: "Diffusion continue 24/7" },
      { icon: Users, title: "DJ System", description: "Rôles DJ, file d'attente collaborative" },
      { icon: Zap, title: "Commandes Rapides", description: "Contrôles intuitifs et réactifs" },
      { icon: BarChart3, title: "Stats Musicales", description: "Historique, favoris, tendances" },
    ],
    demoImages: [
      { url: "/images/radio/demo-1.jpg", title: "Musique en direct : Spotify, YouTube, Deezer, MP3" },
      { url: "/images/radio/demo-2.jpg", title: "Une radio par salon vocal, réglée depuis le site" },
    ]
  }
};

// Couleur du produit en rgba, pour les reflets et les fonds teintés.
const tint = (hex, alpha) => {
  const h = String(hex || "#8b5cf6").replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h.padEnd(6, "0").slice(0, 6);
  const n = parseInt(full, 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
};
const euros = (value) => `${(Number(value) || 0).toFixed(2).replace(".", ",")} €`;
const FEATURE_ICONS = [Zap, Shield, Sparkles, Layers, Rocket, Users, Bell, BarChart3, Lock];

function PricingCard({ bot, pricingPlans, selectedPlan, setSelectedPlan, currentPlan, onBuy }) {
  const accent = bot.color || "#8b5cf6";
  const blocked = saleBlock(bot);
  const included = bot.downloadable
    ? ["Clé de licence personnelle", "Mises à jour incluses", "Téléchargement immédiat", "Support par ticket"]
    : ["Activation instantanée", "Mises à jour incluses", "Réglages depuis le site", "Support par ticket"];
  return (
    <div className="h-full overflow-y-auto rounded-3xl border border-white/[0.08] bg-[#0f1019] p-6 sm:p-7" data-testid="pricing-card">
      <div className="flex items-center gap-3">
        {bot.logo ? <img src={bot.logo} alt="" className="h-12 w-12 rounded-2xl object-cover ring-1 ring-white/10" /> : <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/[0.06]"><bot.icon className="h-6 w-6" /></span>}
        <div className="min-w-0">
          <p className="truncate font-heading text-lg font-bold">{bot.name}</p>
          <p className="text-sm text-white/45">Choisis ta formule</p>
        </div>
      </div>

      <div className={`mt-6 grid gap-2.5 ${pricingPlans.length > 2 ? "sm:grid-cols-2" : pricingPlans.length === 2 ? "grid-cols-2" : ""}`} role="radiogroup" data-testid="pricing-select">
        {pricingPlans.map((plan) => {
          const active = plan.id === currentPlan?.id;
          const highlight = plan.popular || plan.featured || /vie/i.test(plan.duration || "");
          return (
            <button
              key={plan.id}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => setSelectedPlan(plan.id)}
              data-testid={`pricing-plan-${plan.id}`}
              className={`relative rounded-2xl border p-4 text-left transition ${active ? "bg-white/[0.06]" : "border-white/[0.07] bg-white/[0.02] hover:border-white/15 hover:bg-white/[0.04]"}`}
              style={active ? { borderColor: accent, boxShadow: `0 0 0 3px ${tint(accent, 0.18)}` } : undefined}
            >
              {highlight && pricingPlans.length > 1 && (
                <span className="absolute -top-2.5 right-3 rounded-full px-2 py-0.5 text-[10.5px] font-semibold text-white" style={{ background: accent }}>
                  {plan.featured || /vie/i.test(plan.duration || "") ? "Le meilleur choix" : "Populaire"}
                </span>
              )}
              <span className="flex items-center justify-between gap-2">
                <span className="text-sm font-semibold text-white/85">{plan.name}</span>
                <span className={`flex h-4 w-4 items-center justify-center rounded-full border ${active ? "border-transparent" : "border-white/25"}`} style={active ? { background: accent } : undefined}>
                  {active && <Check className="h-3 w-3 text-white" />}
                </span>
              </span>
              <span className="mt-2 block font-heading text-2xl font-bold">{euros(plan.price)}</span>
              <span className="text-xs text-white/45">{plan.duration}{/\d/.test(plan.savings || "") ? ` · -${String(plan.savings).replace(/^-/, "")}` : ""}</span>
            </button>
          );
        })}
      </div>

      <ul className="mt-6 grid gap-2 sm:grid-cols-2">
        {included.map((item) => (
          <li key={item} className="flex items-center gap-2 text-sm text-white/65">
            <Check className="h-4 w-4 shrink-0" style={{ color: accent }} />{item}
          </li>
        ))}
      </ul>

      <div className="mt-6">
        {blocked ? (
          <>
            <Button data-testid="pricing-cta-btn" disabled className={`h-12 w-full rounded-xl font-semibold disabled:opacity-100 cursor-not-allowed ${blocked.cls}`}>
              <AlertTriangle className="mr-2 h-5 w-5" />{blocked.button}
            </Button>
            <p className="mt-3 text-center text-xs text-white/45" data-testid="sale-blocked-text">{blocked.text}</p>
          </>
        ) : bot.tebex_url ? (
          <>
            <Button data-testid="pricing-cta-btn" onClick={onBuy} className="h-12 w-full rounded-xl font-semibold text-white hover:brightness-110" style={{ background: `linear-gradient(135deg, ${accent}, #7c3aed)` }}>
              <ExternalLink className="mr-2 h-5 w-5" />Acheter sur Tebex
            </Button>
            <p className="mt-3 text-center text-xs text-white/45">Paiement sécurisé sur Tebex, le partenaire officiel de FiveM : le script est ajouté à ton compte Cfx.re et se télécharge sur portal.cfx.re.</p>
          </>
        ) : (
          <>
            <Button data-testid="pricing-cta-btn" onClick={onBuy} className="h-12 w-full rounded-xl font-semibold text-white shadow-[0_12px_30px_-12px_rgba(124,58,237,0.8)] hover:brightness-110" style={{ background: `linear-gradient(135deg, ${accent}, #7c3aed)` }}>
              <CreditCard className="mr-2 h-5 w-5" />Acheter pour {euros(currentPlan?.price)}
            </Button>
            <p className="mt-3 flex items-center justify-center gap-1.5 text-xs text-white/40"><ShieldCheck className="h-3.5 w-3.5" />Paiement sécurisé · accès dans ton espace client</p>
          </>
        )}
      </div>
    </div>
  );
}

function PageLayoutBlock({ block, bot, pricingPlans, selectedPlan, setSelectedPlan, currentPlan, onBuy }) {
  return (
    <div style={{ position: "absolute", left: block.x, top: block.y, width: block.width, height: block.height }}>
      {block.type === "image" && (
        <img src={block.url} alt="" className="w-full h-full" style={{ objectFit: block.objectFit || "cover" }} />
      )}
      {block.type === "text" && (
        <div style={{ fontSize: block.fontSize, color: block.color, textAlign: block.align, fontWeight: block.weight, whiteSpace: "pre-wrap" }}>
          {block.content}
        </div>
      )}
      {block.type === "pricing" && (
        <PricingCard bot={bot} pricingPlans={pricingPlans} selectedPlan={selectedPlan} setSelectedPlan={setSelectedPlan} currentPlan={currentPlan} onBuy={onBuy} />
      )}
    </div>
  );
}

function SectionTitle({ eyebrow, title, text, accent }) {
  return (
    <div className="mb-8 max-w-2xl">
      <p className="text-[11px] font-semibold uppercase tracking-[0.16em]" style={{ color: tint(accent, 0.9) }}>{eyebrow}</p>
      <h2 className="mt-2 font-heading text-2xl font-bold tracking-tight sm:text-3xl">{title}</h2>
      {text && <p className="mt-2 text-sm leading-relaxed text-white/50">{text}</p>}
    </div>
  );
}

function Gallery({ images, accent }) {
  const [index, setIndex] = useState(0);
  const [zoom, setZoom] = useState(false);
  const list = images.filter((img) => img.url);
  if (!list.length) return null;
  const current = list[Math.min(index, list.length - 1)];
  return (
    <div data-testid="product-gallery">
      <button type="button" onClick={() => setZoom(true)} className="group relative block w-full overflow-hidden rounded-3xl border border-white/[0.08] bg-black/40" aria-label="Agrandir l'image">
        <img src={current.url} alt={current.title || ""} className="aspect-video w-full object-contain transition duration-500 group-hover:scale-[1.015]" />
        <span className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-3 bg-gradient-to-t from-black/80 via-black/30 to-transparent p-4 pt-12 text-left">
          <span className="text-sm font-medium text-white/90">{current.title}</span>
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/10 text-white/80 backdrop-blur"><Maximize2 className="h-4 w-4" /></span>
        </span>
      </button>
      {list.length > 1 && (
        <div className="mt-3 flex gap-2.5 overflow-x-auto pb-1">
          {list.map((img, i) => (
            <button
              key={img.url}
              type="button"
              onClick={() => setIndex(i)}
              className={`relative h-16 w-28 shrink-0 overflow-hidden rounded-xl border transition sm:h-20 sm:w-36 ${i === index ? "" : "border-white/[0.08] opacity-60 hover:opacity-100"}`}
              style={i === index ? { borderColor: accent, boxShadow: `0 0 0 2px ${tint(accent, 0.25)}` } : undefined}
              aria-label={img.title || `Image ${i + 1}`}
              data-testid={`gallery-thumb-${i}`}
            >
              <img src={img.url} alt="" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      )}
      <Dialog open={zoom} onOpenChange={setZoom}>
        <DialogContent className="max-w-6xl border-white/10 bg-[#0a0a0f] p-2 sm:rounded-2xl">
          <DialogTitle className="sr-only">{current.title || "Image"}</DialogTitle>
          <img src={current.url} alt={current.title || ""} className="max-h-[85vh] w-full rounded-xl object-contain" />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RatingStars({ value }) {
  return (
    <span className="flex">
      {[1, 2, 3, 4, 5].map((n) => <Star key={n} className={`h-4 w-4 ${value >= n - 0.25 ? "fill-yellow-400 text-yellow-400" : "text-white/15"}`} />)}
    </span>
  );
}

// Pricing plans with offset
const BASE_PRICING = [
  { id: "basique", name: "Basique", basePrice: 2, duration: "1 mois", badge: "🥉", savings: null },
  { id: "standard", name: "Standard", basePrice: 4, duration: "2 mois", badge: "🥈", savings: "10%" },
  { id: "premium", name: "Premium", basePrice: 8, duration: "4 mois", badge: "🥇", savings: "20%", popular: true },
  { id: "gold", name: "Gold", basePrice: 16, duration: "8 mois", badge: "💎", savings: "33%" },
  { id: "platinum", name: "Platinum", basePrice: 24, duration: "12 mois", badge: "👑", savings: "50%" },
  { id: "lifetime", name: "Lifetime", basePrice: 46, duration: "À vie", badge: "♾️", savings: "Meilleur investissement", featured: true },
];

export default function BotDetailPage() {
  const { botType } = useParams();
  const navigate = useNavigate();
  useEffect(() => {
    if (PRODUCT_ALIASES[botType]) navigate(`/bot/${PRODUCT_ALIASES[botType]}`, { replace: true });
  }, [botType, navigate]);
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState("login");
  const { user } = useAuth();

  const configuredBot = BOTS_CONFIG[botType];
  const [shopBot, setShopBot] = useState(null);

  useEffect(() => {
    axios.get(`${API}/shop/bots`).then(response => {
      const found = (response.data || []).find(item => item.id === botType);
      if (found) setShopBot(found);
    }).catch(() => {});
  }, [botType]);

  const bot = useMemo(() => {
    if (!configuredBot && !shopBot) return null;
    const base = configuredBot || {
      id: botType, name: shopBot?.name || botType, tagline: shopBot?.downloadable ? "Un script VantaPulse pour votre serveur" : "Un bot VantaPulse pour votre serveur",
      description: "Découvrez les fonctionnalités de ce produit.", logo: "", banner: "", color: shopBot?.color || "#5865F2",
      colorClass: "text-blue-500", bgClass: "bg-blue-500", bgLightClass: "bg-blue-500/10", borderClass: "border-blue-500",
      btnClass: "bg-blue-500 hover:bg-blue-600", glowClass: "shadow-[0_0_30px_rgba(88,101,242,0.3)]", icon: Bot, priceOffset: 0,
      features: [], demoImages: []
    };
    const features = shopBot?.features?.length ? shopBot.features.map(feature => typeof feature === "string" ? { title: feature, description: "Une fonctionnalité intégrée au produit.", icon: Zap } : feature) : base.features;
    const demoImages = shopBot?.demo_images?.length ? shopBot.demo_images.map((image, index) => ({ url: image.url || image, title: image.title || `Démonstration ${index + 1}` })) : base.demoImages;
    const assetDefaults = {
      gestion: { logo: "/images/logo-gestion.svg", banner: "/images/banner-gestion.svg" },
      radio: { logo: "/images/logo-radio.svg", banner: "/images/banner-radio.svg" },
    }[botType] || {};
    return { ...base, ...shopBot, logo: shopBot?.image_url || assetDefaults.logo || base.logo, banner: shopBot?.banner_url || assetDefaults.banner || base.banner, description: shopBot?.description || base.description, features, demoImages };
  }, [botType, configuredBot, shopBot]);

  const [selectedPlan, setSelectedPlan] = useState("premium");
  const [publicPromos, setPublicPromos] = useState([]);

  useEffect(() => {
    axios.get(`${API}/promo-codes/public`).then(res => {
      setPublicPromos(res.data || []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (bot?.plans?.length) setSelectedPlan(bot.plans[0].id || bot.plans[0].name);
  }, [bot]);

  const [stats, setStats] = useState({ count: 0, average: 0 });
  useEffect(() => {
    if (!shopBot?.id) return;
    axios.get(`${API}/reviews/stats`, { params: { product_id: shopBot.id } }).then((r) => setStats(r.data || { count: 0, average: 0 })).catch(() => {});
  }, [shopBot?.id]);

  // Onglet de la barre de sections qui correspond à la partie visible de la page.
  const [activeSection, setActiveSection] = useState("");
  useEffect(() => {
    const sections = [...document.querySelectorAll("[data-product-section]")];
    if (!sections.length || typeof IntersectionObserver === "undefined") return undefined;
    const observer = new IntersectionObserver((entries) => {
      const visible = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
      if (visible[0]) setActiveSection(visible[0].target.id);
    }, { rootMargin: "-130px 0px -55% 0px" });
    sections.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, [bot, shopBot]);

  if (!bot) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0a0a0f] px-6 text-white">
        <div className="text-center">
          <p className="font-heading text-2xl font-bold">Produit introuvable</p>
          <p className="mt-2 text-sm text-white/45">Ce produit n'existe pas ou n'est plus en vente.</p>
          <Link to="/shop" className="mt-6 inline-flex items-center gap-2 rounded-xl bg-white px-4 py-2.5 text-sm font-semibold text-[#0a0a0f] hover:bg-white/90">Voir la boutique<ChevronRight className="h-4 w-4" /></Link>
        </div>
      </div>
    );
  }

  const pricingPlans = bot.plans?.length ? bot.plans.map(plan => ({
    ...plan,
    duration: plan.duration_label || `${plan.duration_days || 0} jours`,
    basePrice: plan.price,
    price: plan.price,
  })) : BASE_PRICING.map(plan => ({
    ...plan,
    price: plan.basePrice + bot.priceOffset,
  }));

  const currentPlan = pricingPlans.find(p => p.id === selectedPlan) || pricingPlans[0];

  const askToRegister = () => {
    setAuthMode("register");
    setShowAuth(true);
  };

  const handleSelectPlan = () => {
    if (saleBlock(bot)) return;
    // Produit FiveM vendu sur Tebex : pas de compte VantaPulse nécessaire.
    if (bot.tebex_url) {
      window.open(bot.tebex_url, "_blank", "noopener,noreferrer");
      return;
    }
    if (!user) {
      setAuthMode("register");
      setShowAuth(true);
    } else {
      navigate(`/checkout?plan=${selectedPlan}&bot=${botType}`);
    }
  };

  const accent = bot.color || "#8b5cf6";
  const productId = shopBot?.id || botType;
  const video = shopBot ? (shopBot.video_url || defaultProductVideo(shopBot.id)) : null;
  const gallery = (bot.demoImages || []).filter((img) => img.url);
  const customLayout = bot.page_layout?.blocks?.length > 0;
  const promos = publicPromos.filter((p) => p.applies_to === "all" || p.applies_to === botType);
  const cheapest = pricingPlans.reduce((min, p) => (min && min.price <= p.price ? min : p), null);
  const latest = shopBot?.versions?.[0]?.version;
  const blocked = saleBlock(bot);
  // Bannière en SVG = simple dégradé : la première capture montre mieux le produit.
  const heroImage = bot.banner && !/\.svg($|\?)/i.test(bot.banner) ? bot.banner : (gallery[0]?.url || bot.banner);
  const tagline = configuredBot || shopBot?.tagline ? bot.tagline : "";
  const kind = bot.downloadable ? "Script FiveM" : "Bot Discord";
  const goTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

  const sections = [
    hasDeviceDemo(productId) && { id: "demo", label: "Démo" },
    !customLayout && bot.features.length > 0 && { id: "fonctionnalites", label: "Fonctionnalités" },
    !customLayout && gallery.length > 0 && { id: "apercu", label: "Aperçu" },
    video && { id: "video", label: "Vidéo" },
    !customLayout && { id: "tarifs", label: "Tarifs" },
    shopBot?.versions?.length > 0 && { id: "nouveautes", label: "Nouveautés" },
    shopBot && { id: "avis", label: "Avis" },
  ].filter(Boolean);
  const second = hasDeviceDemo(productId)
    ? { id: "demo", label: "Essayer en direct", Icon: MousePointerClick }
    : video ? { id: "video", label: "Voir la vidéo", Icon: Play }
      : gallery.length ? { id: "apercu", label: "Voir les images", Icon: Sparkles } : null;
  const trust = [
    { Icon: Zap, label: bot.downloadable ? "Téléchargement immédiat" : "Activation instantanée" },
    { Icon: RefreshCw, label: "Mises à jour incluses" },
    { Icon: Headphones, label: "Support réactif" },
    { Icon: ShieldCheck, label: "Paiement sécurisé" },
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white" data-testid="product-page">
      <Navbar onAuthClick={() => setShowAuth(true)} />

      <main>
        {/* En-tête du produit */}
        <section className="relative overflow-hidden">
          <div className="pointer-events-none absolute inset-0">
            {bot.banner && <img src={bot.banner} alt="" className="h-full w-full scale-110 object-cover opacity-[0.16] blur-2xl" />}
            <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0f]/40 via-[#0a0a0f]/70 to-[#0a0a0f]" />
            <div className="absolute left-1/2 top-[-260px] h-[520px] w-[980px] -translate-x-1/2 rounded-full blur-[110px]" style={{ background: `radial-gradient(closest-side, ${tint(accent, 0.35)}, transparent)` }} />
          </div>

          <div className="relative mx-auto grid max-w-7xl items-center gap-10 px-5 pb-14 pt-24 sm:px-6 lg:grid-cols-[1.05fr_1fr] lg:gap-14 lg:px-8 lg:pt-28">
            <div className="page-rise">
              <nav className="flex items-center gap-1.5 text-xs text-white/40" aria-label="Fil d'Ariane">
                <Link to="/shop" className="hover:text-white">Boutique</Link>
                <ChevronRight className="h-3.5 w-3.5" />
                <span className="truncate text-white/65">{bot.name}</span>
              </nav>

              <div className="mt-6 flex items-center gap-3">
                {bot.logo ? (
                  <img src={bot.logo} alt="" className="h-14 w-14 rounded-2xl object-cover ring-1 ring-white/10" style={{ boxShadow: `0 10px 40px -10px ${tint(accent, 0.7)}` }} />
                ) : (
                  <span className="flex h-14 w-14 items-center justify-center rounded-2xl ring-1 ring-white/10" style={{ background: tint(accent, 0.18) }}><bot.icon className="h-7 w-7" /></span>
                )}
                <div className="flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.05] px-2.5 py-1 text-[11.5px] text-white/70">
                    {bot.downloadable ? <Gamepad2 className="h-3.5 w-3.5" /> : <Bot className="h-3.5 w-3.5" />}{kind}
                  </span>
                  {latest && <span className="rounded-full border border-white/10 bg-white/[0.05] px-2.5 py-1 text-[11.5px] text-white/70">v{latest}</span>}
                  {blocked && <span className={`rounded-full px-2.5 py-1 text-[11.5px] font-medium ${blocked.cls}`}>{blocked.button}</span>}
                </div>
              </div>

              <h1 className="mt-5 font-heading text-4xl font-bold leading-[1.08] tracking-tight sm:text-5xl" data-testid="product-title">{bot.name}</h1>
              {tagline && <p className="mt-3 text-lg font-medium" style={{ color: tint(accent, 0.95) }}>{tagline}</p>}
              <p className="mt-4 max-w-xl text-[15px] leading-relaxed text-white/55">{bot.description}</p>

              <button type="button" onClick={() => goTo("avis")} className="mt-5 flex items-center gap-2 text-sm text-white/55 hover:text-white">
                {stats.count > 0 ? (<><RatingStars value={stats.average} /><span>{stats.average.toFixed(1)} · {stats.count} avis</span></>) : (<><Sparkles className="h-4 w-4 text-amber-300" /><span>Nouveau dans la boutique</span></>)}
              </button>

              <div className="mt-7 flex flex-wrap items-end gap-x-6 gap-y-4">
                {cheapest && (
                  <div>
                    <p className="text-xs text-white/40">{pricingPlans.length > 1 ? "À partir de" : "Prix"}</p>
                    <p className="font-heading text-3xl font-bold" data-testid="product-price">{euros(cheapest.price)}<span className="ml-1.5 text-sm font-normal text-white/40">/ {cheapest.duration}</span></p>
                  </div>
                )}
                <div className="flex flex-wrap gap-2.5">
                  <button
                    type="button"
                    onClick={() => (customLayout ? handleSelectPlan() : goTo("tarifs"))}
                    disabled={!!blocked}
                    data-testid="hero-buy"
                    className="inline-flex h-12 items-center gap-2 rounded-xl px-5 text-sm font-semibold text-white shadow-[0_14px_34px_-14px_rgba(124,58,237,0.9)] transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
                    style={{ background: `linear-gradient(135deg, ${accent}, #7c3aed)` }}
                  >
                    <CreditCard className="h-4 w-4" />{blocked ? blocked.button : "Acheter"}
                  </button>
                  {second && (
                    <button type="button" onClick={() => goTo(second.id)} data-testid="hero-second" className="inline-flex h-12 items-center gap-2 rounded-xl border border-white/10 bg-white/[0.04] px-5 text-sm font-semibold text-white/85 transition hover:bg-white/[0.08] hover:text-white">
                      <second.Icon className="h-4 w-4" />{second.label}
                    </button>
                  )}
                </div>
              </div>

              <div className="mt-7 grid max-w-xl grid-cols-2 gap-x-4 gap-y-2.5">
                {trust.map(({ Icon, label }) => (
                  <span key={label} className="flex items-center gap-2 text-[13px] text-white/50"><Icon className="h-4 w-4 shrink-0" style={{ color: tint(accent, 0.9) }} />{label}</span>
                ))}
              </div>
            </div>

            <div className="page-rise relative" style={{ animationDelay: "90ms" }}>
              <div className="absolute -inset-6 rounded-[2.5rem] opacity-70 blur-3xl" style={{ background: `radial-gradient(closest-side, ${tint(accent, 0.35)}, transparent)` }} />
              <div className="relative overflow-hidden rounded-3xl border border-white/[0.09] bg-[#0f1019] shadow-2xl">
                {heroImage ? (
                  <img src={heroImage} alt={bot.name} className="aspect-[16/10] w-full object-cover" data-testid="product-hero-image" />
                ) : (
                  <div className="flex aspect-[16/10] w-full items-center justify-center" style={{ background: `linear-gradient(135deg, ${tint(accent, 0.35)}, transparent)` }}><bot.icon className="h-20 w-20 text-white/70" /></div>
                )}
              </div>
              {(bot.features.length > 0 || gallery.length > 0) && (
                <div className="absolute -bottom-5 left-5 flex items-center gap-3 rounded-2xl border border-white/10 bg-[#0f1019]/90 px-4 py-3 shadow-xl backdrop-blur-xl">
                  <span className="flex h-9 w-9 items-center justify-center rounded-xl" style={{ background: tint(accent, 0.18), color: accent }}><Layers className="h-4 w-4" /></span>
                  <div className="text-xs">
                    <p className="font-semibold text-white/90">{bot.features.length > 0 ? `${bot.features.length} fonctionnalités clés` : `${gallery.length} captures`}</p>
                    <p className="text-white/45">{latest ? `Version ${latest}` : kind}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Barre des sections */}
        {sections.length > 1 && (
          <div className="sticky top-16 z-30 border-y border-white/[0.06] bg-[#0a0a0f]/80 backdrop-blur-xl">
            <div className="mx-auto flex max-w-7xl items-center gap-1 overflow-x-auto px-5 sm:px-6 lg:px-8">
              {sections.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => goTo(s.id)}
                  data-testid={`section-tab-${s.id}`}
                  className={`relative shrink-0 px-3 py-3.5 text-sm transition ${activeSection === s.id ? "text-white" : "text-white/45 hover:text-white/80"}`}
                >
                  {s.label}
                  {activeSection === s.id && <span className="absolute inset-x-3 bottom-0 h-0.5 rounded-full" style={{ background: accent }} />}
                </button>
              ))}
              {!blocked && !customLayout && (
                <button type="button" onClick={() => goTo("tarifs")} className="ml-auto hidden shrink-0 items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold text-white sm:inline-flex" style={{ background: tint(accent, 0.25) }}>
                  {cheapest ? `Dès ${euros(cheapest.price)}` : "Acheter"}<ChevronRight className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>
        )}

        <div className="mx-auto max-w-7xl space-y-20 px-5 py-14 sm:px-6 lg:px-8">
          {(promos.length > 0 || shopBot) && (
            <div className="space-y-3">
              {promos.map((promo) => (
                <div key={promo.code} className="flex flex-wrap items-center gap-3 rounded-2xl border px-4 py-3 text-sm" style={{ borderColor: tint(accent, 0.3), background: tint(accent, 0.07) }}>
                  <Tag className="h-4 w-4 shrink-0" style={{ color: accent }} />
                  <span className="text-white/75">Offre en cours : code <code className="rounded-md bg-black/30 px-2 py-0.5 font-mono font-bold text-white">{promo.code}</code> pour {promo.discount_type === "percentage" ? `${promo.discount_value} % de réduction` : `${promo.discount_value} € de réduction`}</span>
                </div>
              ))}
              {shopBot && <UpgradeCard product={shopBot} user={user} />}
              {shopBot && <TrialCard product={shopBot} user={user} onNeedAuth={askToRegister} />}
            </div>
          )}

          {hasDeviceDemo(productId) && (
            <section id="demo" data-product-section className="scroll-mt-32">
              <DeviceDemo productId={productId} />
            </section>
          )}

          {customLayout ? (
            /* Mise en page personnalisée (Panel admin > Boutique > Personnaliser la page).
               Deux canevas indépendants (ordinateur / téléphone) permutés en CSS pur selon
               le viewport réel, pour coller exactement à l'aperçu du PageBuilder. La mise en
               page téléphone retombe sur celle de l'ordinateur si elle n'a jamais été
               enregistrée (anciennes pages personnalisées avant l'ajout du mode téléphone). */
            <section>
              <div className="hidden md:flex justify-center overflow-x-auto">
                <div className="mx-auto" style={{ width: 1200, maxWidth: "100%" }}>
                  <div style={{ position: "relative", height: bot.page_layout.canvas_height || 1400 }}>
                    {bot.page_layout.blocks.map((block) => (
                      <PageLayoutBlock key={block.id} block={block} bot={bot} pricingPlans={pricingPlans} selectedPlan={selectedPlan} setSelectedPlan={setSelectedPlan} currentPlan={currentPlan} onBuy={handleSelectPlan} />
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex md:hidden justify-center overflow-x-auto">
                <div className="mx-auto" style={{ width: 390, maxWidth: "100%" }}>
                  <div style={{ position: "relative", height: (bot.page_layout.mobile_blocks?.length ? bot.page_layout.mobile_canvas_height : bot.page_layout.canvas_height) || 1400 }}>
                    {(bot.page_layout.mobile_blocks?.length ? bot.page_layout.mobile_blocks : bot.page_layout.blocks).map((block) => (
                      <PageLayoutBlock key={block.id} block={block} bot={bot} pricingPlans={pricingPlans} selectedPlan={selectedPlan} setSelectedPlan={setSelectedPlan} currentPlan={currentPlan} onBuy={handleSelectPlan} />
                    ))}
                  </div>
                </div>
              </div>
            </section>
          ) : (
            <>
              {bot.features.length > 0 && (
                <section id="fonctionnalites" data-product-section className="scroll-mt-32">
                  <SectionTitle accent={accent} eyebrow="Fonctionnalités" title={`Tout ce que fait ${bot.name}`} text={bot.downloadable ? "Ce que tes joueurs retrouvent en jeu dès l'installation." : "Ce que ton serveur Discord gagne dès l'activation."} />
                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {bot.features.map((feature, i) => {
                      const Icon = feature.icon && feature.icon !== Zap ? feature.icon : FEATURE_ICONS[i % FEATURE_ICONS.length];
                      return (
                        <div key={feature.title} className="shop-rise group rounded-2xl border border-white/[0.07] bg-[#0f1019] p-5 transition hover:-translate-y-0.5 hover:border-white/15" style={{ animationDelay: `${i * 45}ms` }} data-testid="product-feature">
                          <span className="flex h-10 w-10 items-center justify-center rounded-xl transition group-hover:scale-105" style={{ background: tint(accent, 0.14), color: accent }}><Icon className="h-5 w-5" /></span>
                          <p className="mt-4 font-semibold leading-snug">{feature.title}</p>
                          {feature.description && feature.description !== "Une fonctionnalité intégrée au produit." && <p className="mt-1.5 text-sm leading-relaxed text-white/50">{feature.description}</p>}
                        </div>
                      );
                    })}
                  </div>
                </section>
              )}

              {gallery.length > 0 && (
                <section id="apercu" data-product-section className="scroll-mt-32">
                  <SectionTitle accent={accent} eyebrow="Aperçu" title="En images" text="Clique sur une image pour l'agrandir." />
                  <Gallery images={gallery} accent={accent} />
                </section>
              )}
            </>
          )}

          {video && (
            <section id="video" data-product-section className="scroll-mt-32">
              <SectionTitle accent={accent} eyebrow="Vidéo" title="Le produit en action" />
              <ProductVideo url={video} title={bot.name} heading="" />
            </section>
          )}

          {!customLayout && (
            <section id="tarifs" data-product-section className="scroll-mt-32">
              <div className="grid items-start gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:gap-12">
                <div className="lg:sticky lg:top-32">
                  <SectionTitle accent={accent} eyebrow="Tarifs" title="Choisis ta formule" text={`Toutes les formules donnent accès à ${bot.name} en entier.${bot.priceOffset < 0 ? " Prix réduits sur ce produit !" : ""}`} />
                  <div className="space-y-3">
                    {[
                      { Icon: Rocket, title: bot.downloadable ? "Prêt en quelques minutes" : "En ligne tout de suite", text: bot.downloadable ? "Ta clé et le fichier t'attendent dans ton espace client juste après le paiement." : "Ton bot démarre après le paiement, tu le règles depuis le site." },
                      { Icon: RefreshCw, title: "Mises à jour incluses", text: "Les nouvelles versions arrivent dans ton espace client." },
                      { Icon: Headphones, title: "Une équipe qui répond", text: "Un souci ? L'assistant et le support par ticket sont là." },
                    ].map(({ Icon, title, text }) => (
                      <div key={title} className="flex gap-3 rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4">
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/[0.05]" style={{ color: accent }}><Icon className="h-4 w-4" /></span>
                        <div><p className="text-sm font-semibold">{title}</p><p className="mt-0.5 text-[13px] text-white/50">{text}</p></div>
                      </div>
                    ))}
                    {Number(shopBot?.trial_days) > 0 && !blocked && (
                      <p className="flex items-center gap-2 px-1 text-[13px] text-emerald-300/90"><Gift className="h-4 w-4" />Pas sûr ? Essaie-le gratuitement {shopBot.trial_days} jours.</p>
                    )}
                  </div>
                </div>
                <PricingCard bot={bot} pricingPlans={pricingPlans} selectedPlan={selectedPlan} setSelectedPlan={setSelectedPlan} currentPlan={currentPlan} onBuy={handleSelectPlan} />
              </div>
            </section>
          )}

          {shopBot && (
            <section className="overflow-hidden rounded-3xl border border-white/[0.07] bg-[#0f1019] px-4 py-10 sm:px-8" data-testid="product-review-carousel">
              <ReviewCarousel productId={shopBot.id} title="Les avis des clients" emptyLink="#avis" emptyText="Tu utilises ce produit ? Ton avis aide les autres à choisir." />
            </section>
          )}

          {shopBot && (
            <div className="grid items-start gap-6 lg:grid-cols-[0.85fr_1.15fr]">
              {shopBot.versions?.length > 0 && (
                <section id="nouveautes" data-product-section className="scroll-mt-32 rounded-3xl border border-white/[0.07] bg-[#0f1019] p-6 sm:p-7">
                  <ProductChangelog versions={shopBot.versions} />
                </section>
              )}
              <section id="avis" data-product-section className={`scroll-mt-32 rounded-3xl border border-white/[0.07] bg-[#0f1019] p-6 sm:p-7 ${shopBot.versions?.length ? "" : "lg:col-span-2"}`}>
                <ProductReviews productId={shopBot.id} user={user} onNeedAuth={askToRegister} />
              </section>
            </div>
          )}

          {/* Appel final */}
          <section className="relative overflow-hidden rounded-3xl border border-white/[0.08] p-8 text-center sm:p-12" style={{ background: `linear-gradient(135deg, ${tint(accent, 0.18)}, rgba(124,58,237,0.12) 55%, rgba(15,16,25,0.9))` }}>
            <div className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full blur-3xl" style={{ background: tint(accent, 0.3) }} />
            <div className="relative">
              {bot.logo && <img src={bot.logo} alt="" className="mx-auto h-14 w-14 rounded-2xl object-cover ring-1 ring-white/15" />}
              <h2 className="mt-5 font-heading text-2xl font-bold tracking-tight sm:text-3xl">Prêt à lancer {bot.name} ?</h2>
              <p className="mx-auto mt-2 max-w-lg text-sm text-white/55">{bot.downloadable ? "Installe-le sur ton serveur dès aujourd'hui." : "Transforme ton serveur Discord dès aujourd'hui."}</p>
              <div className="mt-7 flex flex-wrap justify-center gap-3">
                {blocked ? (
                  <Button data-testid="cta-activate" disabled className={`h-12 rounded-xl px-6 font-semibold disabled:opacity-100 cursor-not-allowed ${blocked.cls}`}>
                    <AlertTriangle className="mr-2 h-5 w-5" />{blocked.button}
                  </Button>
                ) : (
                  <Button data-testid="cta-activate" onClick={customLayout ? handleSelectPlan : () => goTo("tarifs")} className="h-12 rounded-xl bg-white px-6 font-semibold text-[#0a0a0f] hover:bg-white/90">
                    {customLayout ? "Activer maintenant" : "Voir les tarifs"}<ChevronRight className="ml-1.5 h-4 w-4" />
                  </Button>
                )}
                <Link to="/support" className="inline-flex h-12 items-center gap-2 rounded-xl border border-white/15 px-6 text-sm font-semibold text-white/85 hover:bg-white/[0.06]">
                  <MessageSquare className="h-4 w-4" />Une question ?
                </Link>
              </div>
            </div>
          </section>
        </div>
      </main>

      <SiteFooter />

      <AuthModal
        isOpen={showAuth}
        onClose={() => setShowAuth(false)}
        mode={authMode}
        setMode={setAuthMode}
      />
    </div>
  );
}
